/**
 * HTTP API client for the respondent surface.
 *
 * Wraps `fetch` with:
 *   - Same-origin cookie auth (`credentials: "include"`)
 *   - Double-submit CSRF: reads `dca_csrf` cookie, attaches `X-CSRF-Token`
 *   - One-shot 401-recovery: tries POST /r/auth/refresh, then retries the
 *     original request once.
 *   - Structured ApiError on non-2xx (JSON body parsed when present).
 *
 * Same-origin requirement: in dev Vite proxies the API; in prod Caddy serves
 * the SPA and API on the same host (ADR-0006). No CORS is needed.
 *
 * This is a per-app copy of the productive client (the established
 * web/productive ↔ web/dashboard precedent — see ADR-0040 / PLAN §3.1: we
 * don't introduce a shared FE package for one duplicated module). The only
 * difference from productive is the refresh endpoint: `/r/auth/refresh`.
 */

import { toast } from "@clones/ui";

const API_BASE = ""; // same-origin
const CSRF_COOKIE = "dca_csrf";
const CSRF_HEADER = "X-CSRF-Token";
const REFRESH_PATH = "/r/auth/refresh";

/** Phase 5 — the structured code the server returns (403) when an admin's
 *  read-only impersonation session attempts a mutating respondent action. */
export const IMPERSONATION_READ_ONLY_CODE = "impersonation.read_only";
/** §6.9 — shown verbatim on any blocked action while impersonating. */
const IMPERSONATION_READ_ONLY_TOAST = "В режиме просмотра действия недоступны.";

export interface ApiErrorBody {
  error: string;
  message: string;
  detail?: unknown;
}

/** True when an error is the server's read-only-impersonation refusal. */
export function isImpersonationReadOnly(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 403) return false;
  const detail = err.body?.detail;
  return (
    typeof detail === "object" &&
    detail !== null &&
    (detail as { code?: unknown }).code === IMPERSONATION_READ_ONLY_CODE
  );
}

export class ApiError extends Error {
  status: number;
  body: ApiErrorBody | null;

  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.message ?? fallback);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
  return m ? decodeURIComponent(m[1]!) : null;
}

interface FetchOpts extends Omit<RequestInit, "headers"> {
  headers?: Record<string, string>;
  /** When true, do not attempt 401 → refresh recovery. Used by /r/auth/refresh itself. */
  skipRefresh?: boolean;
}

let refreshInflight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshInflight) return refreshInflight;
  refreshInflight = (async () => {
    try {
      const res = await rawFetch(REFRESH_PATH, { method: "POST", skipRefresh: true });
      return res.ok;
    } catch {
      return false;
    } finally {
      refreshInflight = null;
    }
  })();
  return refreshInflight;
}

async function rawFetch(path: string, opts: FetchOpts = {}): Promise<Response> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };

  // Tag every API request so the edge proxy (Caddy `@api` matcher) can
  // distinguish a JSON API call from a browser hard-reload of the same path.
  if (!headers["Accept"] && !headers["accept"]) {
    headers["Accept"] = "application/json";
  }

  // Default JSON content-type only when sending a body that's not FormData.
  if (
    opts.body &&
    !(opts.body instanceof FormData) &&
    !headers["Content-Type"] &&
    !headers["content-type"]
  ) {
    headers["Content-Type"] = "application/json";
  }

  // CSRF: attach on every state-changing request when the cookie is present.
  const method = (opts.method ?? "GET").toUpperCase();
  const stateChanging = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";
  if (stateChanging) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf && !headers[CSRF_HEADER]) headers[CSRF_HEADER] = csrf;
  }

  return fetch(API_BASE + path, { ...opts, headers, credentials: "include" });
}

/**
 * Fetch with structured error handling and 401-refresh recovery.
 * Returns the parsed JSON body on success; throws ApiError on failure.
 */
export async function apiFetch<T>(path: string, opts: FetchOpts = {}): Promise<T> {
  let res = await rawFetch(path, opts);

  if (res.status === 401 && !opts.skipRefresh) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      res = await rawFetch(path, opts);
    }
  }

  if (!res.ok) {
    let body: ApiErrorBody | null = null;
    try {
      const text = await res.text();
      body = text ? (JSON.parse(text) as ApiErrorBody) : null;
    } catch {
      /* non-JSON body */
    }
    const err = new ApiError(res.status, body, `${res.status} ${res.statusText}`);
    // Phase 5 — a single, uniform notice for EVERY blocked action while an
    // admin views read-only (composer, nav, settings…), so no call site can
    // forget it. The caller still receives the thrown error to abort its flow.
    if (isImpersonationReadOnly(err)) toast.error(IMPERSONATION_READ_ONLY_TOAST);
    throw err;
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
